import { useEffect, useRef, type RefObject } from 'react';
import { NavLink } from 'react-router';
import { Temavelger } from '../tema/Temavelger.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Merkehode } from './Merke.tsx';

/**
 * Veiledningen for bidragsytere på produktsiden (tech-decisions «Veiledningene for panelbrukere
 * er plattformens tekst», 2026-10-29). Én side for alle leietakere, så adressen er en konstant.
 */
export const VEILEDNING_URL = 'https://innspel.no/veiledning/bidragsyter';

/**
 * Menyen «⋯» (P5, `ux-flow-architect` «Meny fra en knapp»). Panelet har ett dialogmønster
 * fra før — `Dialog.tsx`, som er modal: fokusfelle, `aria-modal`, scrim og sidens taster
 * blokkert. Dette er det ANDRE mønsteret, og forskjellene er krav, ikke smak:
 *
 * - `role="dialog"` UTEN `aria-modal`, ingen fokusfelle, ingen scrim. Siden bak er
 *   fortsatt i bruk — menyen er et sted å bytte palett eller logge ut, ikke en oppgave.
 * - `aria-expanded` på «⋯»-knappen bærer tilstanden; `aria-labelledby` peker på
 *   overskriften «Logget inn som».
 * - Esc og pekertrykk utenfor lukker, og fokus går tilbake til «⋯».
 * - Fokus flyttes INN ved åpning, ellers er menyen utastbar.
 * - **Sidens taster blokkeres IKKE.** `j`, `k` og Enter hører saken til, og `g s` skal
 *   virke med menyen åpen. Derfor ingen `stopPropagation` her.
 * - Rendres UTENFOR sidemenyens rullefelt — den er et søsken av `<aside>` i `.panel`,
 *   ikke et barn (avvik 14). I et kort vindu ville den ellers blitt klippet.
 *
 * Over 900 px legger den seg over foten; under 900 px er den et ark fra bunnen, og
 * bærer da to rader til: Organisasjon (bare eier — den er ikke en fane i rommet) og
 * Tastaturkart med `?`, fordi sidens kart-ikon ikke finnes der (P4, avvik 15).
 */
export function Meny({
  smal,
  eier,
  flereRom,
  rolle,
  utenRom = false,
  visningsnavn,
  veiledning = false,
  adresse,
  leietaker,
  bygg,
  onLukk,
  onLoggUt,
  onKart,
}: {
  smal: boolean;
  eier: boolean;
  flereRom: boolean;
  rolle: string | undefined;
  /** Et medlem uten rom: linjen sier «medlem · uten rom», fra `access` (DD-01 presisert). */
  utenRom?: boolean;
  /**
   * Rommet Hypoteser (H2.2, DD-81): visningsnavnet fra «meg», med «Endre». Navnet står på
   * det hun sender inn; W1 lover at det kan endres her. Utelatt uten rommet.
   */
  visningsnavn?: { navn: string | undefined; onEndre: () => void };
  /** Lenken «Veiledning» for alle med rommet Hypoteser — siden handler om det rommet. */
  veiledning?: boolean;
  adresse: string | null;
  leietaker: string | undefined;
  bygg: string;
  onLukk: () => void;
  onLoggUt: () => void;
  onKart: () => void;
}) {
  const rot = useRef<HTMLDivElement>(null);

  // Fokus INN ved åpning. Fokus TILBAKE gjør `onLukk` i skallet, så begge veier er ett sted.
  useEffect(() => {
    rot.current?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();
  }, []);

  // Lytterne står på document og ikke på en scrim: menyen er ikke modal, og siden bak
  // skal fortsatt kunne brukes. Esc stopper ikke videre — sidens taster er sidens.
  useEffect(() => {
    const ned = (e: PointerEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el && (el.closest('[data-meny]') || el.closest('[data-meny-knapp]'))) return;
      onLukk();
    };
    const tast = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onLukk();
    };
    document.addEventListener('pointerdown', ned);
    document.addEventListener('keydown', tast);
    return () => {
      document.removeEventListener('pointerdown', ned);
      document.removeEventListener('keydown', tast);
    };
  }, [onLukk]);

  return (
    <div ref={rot} data-meny="" data-testid="meny" role="dialog" aria-labelledby="meny-h" className={smal ? 'panel-meny panel-meny-ark' : 'panel-meny'}>
      {/* Med to rom bor identiteten her (DD-48): stripen har gitt plassen til bryteren. */}
      {smal && flereRom ? (
        <div className="panel-meny-merke">
          <Merkehode storrelse="md" linje>
            <span className="panel-merke-skille" aria-hidden="true">
              ·
            </span>
            <span className="panel-merke-leietaker">{leietaker ?? ' '}</span>
          </Merkehode>
        </div>
      ) : null}

      {/* Overskriften er «Logget inn som» (P1). Rollen kommer fra panelets «meg»-kall,
          adressen fra AdminAuth-porten — to kilder, én linje, bare for brukeren selv (DD-68). */}
      <div className="panel-meny-hode">
        <div id="meny-h" className="panel-meny-etikett">
          Logget inn som
        </div>
        <div className="panel-meny-konto">
          <b>{rolle ?? ' '}</b>
          {utenRom ? (
            <>
              <span aria-hidden="true">·</span>
              <span>uten rom</span>
            </>
          ) : null}
          {adresse ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{adresse}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* «Min innlogging» i BEGGE bredder, rett under kontoen den gjelder: siden er brukerens
          egen, ikke rommets og ikke leietakerens, og menyen er det ene stedet i skallet som
          alt handler om «meg». Den har ingen snarvei — `g`-tastene er sidene i rommet. */}
      {/* DD-81: med navn «Visningsnavn · navnet · Endre» (navnet står ferdig fra første
          innlogging, H2.9); uten navn bare «Sett visningsnavn». */}
      {visningsnavn ? (
        <div className="panel-meny-navn" data-testid="meny-visningsnavn">
          {visningsnavn.navn ? (
            <span className="panel-meny-navn-tekst">
              <span className="panel-meny-etikett">Visningsnavn</span>
              <b>{visningsnavn.navn}</b>
            </span>
          ) : null}
          <button type="button" className="panel-knapp-lenke" onClick={visningsnavn.onEndre}>
            {visningsnavn.navn ? 'Endre' : 'Sett visningsnavn'}
          </button>
        </div>
      ) : null}

      <NavLink to="/min-innlogging" className="panel-meny-rad" onClick={onLukk} data-testid="meny-min-innlogging">
        <Ikon navn="shield-checkmark-outline" />
        Min innlogging
      </NavLink>

      {/* Veiledningen ligger på produktsiden, i ny fane og uten referrer — panelet lekker aldri
          adressen sin videre. Bare for dem som har rommet siden handler om. */}
      {veiledning ? (
        <a href={VEILEDNING_URL} target="_blank" rel="noopener noreferrer" className="panel-meny-rad" onClick={onLukk} data-testid="meny-veiledning">
          <Ikon navn="open-outline" />
          Veiledning
        </a>
      ) : null}

      {/* Organisasjon bare under 900 px — over grensen står den i sidemenyen (DD-01). */}
      {smal && eier ? (
        <NavLink to="/organisasjon" className="panel-meny-rad" aria-keyshortcuts="g o" onClick={onLukk}>
          <Ikon navn="business-outline" />
          Organisasjon
        </NavLink>
      ) : null}

      <div className="panel-meny-palett">
        <Temavelger />
      </div>

      {/* Kartet har ÉN tilstand, i skallet (P4): sidens ikon over 900 px, denne raden under. */}
      {smal ? (
        <button type="button" className="panel-meny-rad" aria-keyshortcuts="?" onClick={onKart}>
          <Ikon navn="keypad-outline" />
          Tastaturkart
          <kbd>?</kbd>
        </button>
      ) : null}

      <button type="button" className="panel-meny-rad" onClick={onLoggUt}>
        <Ikon navn="log-out-outline" />
        Logg ut
      </button>

      {/* Byggets identitet. Panelet oppdaterer seg selv, så versjonen alene sier lite —
          sha-en er det man kan slå opp når noen melder fra om noe. */}
      <div className="panel-bygg" data-testid="bygg">
        {bygg}
      </div>
    </div>
  );
}

/**
 * «⋯»-knappen. `aria-label` er «Mer» og ingenting mer: etiketten lover ikke innholdet
 * (avvik 26). `aria-expanded` bærer tilstanden (P5).
 */
export function MerKnapp({ aapen, knappRef, onClick }: { aapen: boolean; knappRef: RefObject<HTMLButtonElement | null>; onClick: () => void }) {
  return (
    <button ref={knappRef} type="button" data-meny-knapp="" className={aapen ? 'panel-mer panel-mer-aapen' : 'panel-mer'} aria-label="Mer" aria-expanded={aapen} onClick={onClick} data-testid="mer">
      <Ikon navn="ellipsis-horizontal-outline" />
    </button>
  );
}
